import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { v1Client } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import { useProviders, type Provider } from '@renderer/hooks/use-providers'
import { liveContextKeys } from '@renderer/hooks/useLiveContext'

const WRITING_STYLE_SUFFIX = '-Writing-Style'

export interface WritingStyleIntegration {
  slug: string
  name: string
  environmentName: string
  provider?: Provider
}

/**
 * Inverse of the backend's `getWritingStyleEnvironmentName`, which capitalizes
 * the toolset artifact name (e.g. `google_docs` -> `Google_docs-Writing-Style`).
 */
function slugFromEnvironmentName(environmentName: string): string | null {
  if (!environmentName.endsWith(WRITING_STYLE_SUFFIX)) {
    return null
  }
  return environmentName.slice(0, -WRITING_STYLE_SUFFIX.length).toLowerCase().replace(/_/g, '-')
}

function writingStyleEnvironmentName(slug: string): string {
  const artifactName = slug.replace(/-/g, '_')
  return `${artifactName.charAt(0).toUpperCase()}${artifactName.slice(1)}${WRITING_STYLE_SUFFIX}`
}

// Artifact names mostly match catalog ids; Gmail's catalog id is `google-gmail`.
function findProvider(providers: Array<Provider> | undefined, slug: string): Provider | undefined {
  return providers?.find((provider) => provider.id === slug || provider.id === `google-${slug}`)
}

function titleCase(slug: string): string {
  return slug
    .split('-')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ')
}

function toIntegration(slug: string, providers: Array<Provider> | undefined): WritingStyleIntegration {
  const provider = findProvider(providers, slug)
  return {
    slug,
    name: provider?.name ?? titleCase(slug),
    environmentName: writingStyleEnvironmentName(slug),
    provider,
  }
}

/** Integrations Highlight has actually learned a writing style for. */
export function useWritingStyleIntegrations(): { integrations: Array<WritingStyleIntegration>; isLoading: boolean } {
  const { getAuthToken } = useAuth()
  const { data: providers, isLoading: isLoadingProviders } = useProviders()

  const query = useQuery({
    queryKey: liveContextKeys.environments(),
    queryFn: async (): Promise<Array<string>> => {
      const response = await v1Client.GET('/live-context', {
        // An empty name disables the server's environment filter; omitting it defaults to 'Default'.
        params: { query: { environmentName: '', list: true } },
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error) {
        throw new Error('Failed to list live context environments')
      }
      const items = Array.isArray(response.data.data) ? response.data.data : []
      return [...new Set(items.map((item) => item.environmentName))]
    },
  })

  const integrations = useMemo(() => {
    const slugs = (query.data ?? []).flatMap((name) => slugFromEnvironmentName(name) ?? [])
    return slugs.map((slug) => toIntegration(slug, providers)).sort((a, b) => a.name.localeCompare(b.name))
  }, [query.data, providers])

  return { integrations, isLoading: query.isLoading || isLoadingProviders }
}

export function useWritingStyleIntegration(slug: string): WritingStyleIntegration {
  const { data: providers } = useProviders()
  return useMemo(() => toIntegration(slug, providers), [slug, providers])
}
